const MB = 1024 * 1024;

const PLATFORM_RULES = {
  whatsapp: {
    image: {
      maxBytes: 5 * MB,
      mimes: new Set(["image/jpeg", "image/jpg", "image/png"]),
      exts: new Set([".jpg", ".jpeg", ".png"]),
    },
    video: {
      maxBytes: 16 * MB,
      mimes: new Set(["video/mp4", "video/3gpp"]),
      exts: new Set([".mp4", ".3gp", ".3gpp"]),
    },
    document: {
      maxBytes: 100 * MB,
      mimes: new Set([
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/plain",
      ]),
      exts: new Set([".pdf", ".doc", ".docx", ".ppt", ".pptx", ".xls", ".xlsx", ".txt"]),
    },
    audio: {
      maxBytes: 16 * MB,
      mimes: new Set([
        "audio/aac",
        "audio/mp4",
        "audio/amr",
        "audio/mpeg",
        "audio/ogg",
      ]),
      exts: new Set([".aac", ".mp4", ".amr", ".mp3", ".mpeg", ".ogg"]),
    },
  },
  instagram: {
    image: {
      maxBytes: 8 * MB,
      mimes: new Set(["image/png", "image/jpeg", "image/jpg", "image/gif"]),
      exts: new Set([".png", ".jpg", ".jpeg", ".gif"]),
    },
    video: {
      maxBytes: 25 * MB,
      mimes: new Set(["video/mp4", "video/ogg", "video/x-msvideo", "video/quicktime", "video/webm"]),
      exts: new Set([".mp4", ".ogg", ".avi", ".mov", ".webm"]),
    },
    document: {
      maxBytes: 25 * MB,
      mimes: new Set(["application/pdf"]),
      exts: new Set([".pdf"]),
    },
    audio: {
      maxBytes: 25 * MB,
      mimes: new Set(["audio/aac", "audio/x-m4a", "audio/mp4", "audio/wav"]),
      exts: new Set([".aac", ".m4a", ".mp4", ".wav"]),
    },
  },
  facebook: {
    image: {
      maxBytes: 8 * MB,
      mimes: new Set(["image/png", "image/jpeg", "image/jpg", "image/gif"]),
      exts: new Set([".png", ".jpg", ".jpeg", ".gif"]),
    },
    video: {
      maxBytes: 25 * MB,
      mimes: new Set(["video/mp4", "video/ogg", "video/x-msvideo", "video/quicktime", "video/webm"]),
      exts: new Set([".mp4", ".ogg", ".avi", ".mov", ".webm"]),
    },
    document: {
      maxBytes: 25 * MB,
      mimes: new Set([
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "text/plain",
        "text/csv",
      ]),
      exts: new Set([".pdf", ".doc", ".docx", ".txt", ".csv"]),
    },
    audio: {
      maxBytes: 25 * MB,
      mimes: new Set([
        "audio/aac",
        "audio/x-m4a",
        "audio/mp4",
        "audio/wav",
        "audio/mpeg",
        "audio/mp3",
      ]),
      exts: new Set([".aac", ".m4a", ".mp4", ".wav", ".mp3", ".mpeg"]),
    },
  },
};

const resolvePlatform = (platform, isTeamMemberChat = false) => {
  if (isTeamMemberChat) return "whatsapp";
  if (platform === "instagram" || platform === "facebook") return platform;
  return "whatsapp";
};

const getExt = (name = "") => {
  const idx = name.lastIndexOf(".");
  if (idx < 0) return "";
  return name.slice(idx).toLowerCase();
};

const hasMatch = (file, rule) => {
  const mime = (file?.type || "").toLowerCase();
  const ext = getExt(file?.name || "");
  return rule.mimes.has(mime) || rule.exts.has(ext);
};

const formatMaxMb = (bytes) => `${Math.round(bytes / MB)} MB`;

const getAllowedHint = (platform) => {
  if (platform === "instagram") {
    return "Instagram supports Image (PNG, JPEG, JPG, GIF), Video (MP4, OGG, AVI, MOV, WebM), Audio (AAC, M4A, MP4, WAV), Document (PDF).";
  }
  if (platform === "facebook") {
    return "Facebook supports Image (PNG, JPEG, JPG, GIF), Video (MP4, OGG, AVI, MOV, WebM), Audio (AAC, M4A, MP4, WAV, MP3), Document (PDF, DOC, DOCX, TXT, CSV).";
  }
  return "WhatsApp supports Image (JPEG, JPG, PNG), Video (MP4, 3GPP), Audio (AAC, MP4, AMR, MPEG/MP3, OGG), Document (PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX, TXT).";
};

export const getInboxAcceptString = (platform, isTeamMemberChat = false) => {
  const resolved = resolvePlatform(platform, isTeamMemberChat);
  const rules = PLATFORM_RULES[resolved];
  const allMimes = new Set([
    ...rules.image.mimes,
    ...rules.video.mimes,
    ...rules.audio.mimes,
    ...rules.document.mimes,
  ]);
  return Array.from(allMimes).join(",");
};

export const classifyInboxMediaFile = (file, platform, isTeamMemberChat = false) => {
  if (!file || !file.size) {
    return { valid: false, error: "No file selected." };
  }

  const resolved = resolvePlatform(platform, isTeamMemberChat);
  const rules = PLATFORM_RULES[resolved];
  const orderedTypes = ["image", "video", "audio", "document"];
  const fileType = (file.type || "").toLowerCase();
  const ext = getExt(file.name || "");

  for (const type of orderedTypes) {
    const rule = rules[type];
    if (!hasMatch(file, rule)) continue;
    if (file.size > rule.maxBytes) {
      return {
        valid: false,
        error: `${type[0].toUpperCase() + type.slice(1)} must be ${formatMaxMb(rule.maxBytes)} or less.`,
      };
    }
    return {
      valid: true,
      kind: type,
      normalizedMime: fileType || ext || "unknown",
    };
  }

  return { valid: false, error: getAllowedHint(resolved) };
};
